import { BLOCKS } from '@contentful/rich-text-types'
import { getConfig } from '../../../config'
import {
  AllowedPageSection,
  ComponentCtaButtonColor,
  ComponentCtaButtonType,
  TypeComponentCtaButton,
  TypePage,
  TypePageFields,
  TypeSectionGroup,
  TypeSectionHero,
  TypeSectionSingleColumn,
} from '../../types'
import {
  ComponentContentTypes,
  isTypeComponentCtaButton,
  isTypeComponentDynamic,
  isTypeSectionGroup,
  isTypeSectionHero,
  isTypeSectionSingleColumn,
} from '../../types/content-types'
import { createEntry } from '../../utils/contentful-entry'
import { patchCTAWithDefaultColor } from '../utils'
import { useGetAuthAndDeviceTypeCta, ctaArgType } from '../../hooks/auth-and-device-type-cta'
import { PatchPageStructure } from '../types'

const config = getConfig()
const signupUrl = `${config.urls.main}/signup?flow_source=extension_download_pages`

// #ToDo improve typings to avoid namespace usage. Replace with the named export and import * as BrowserPagesSignUpFlowExperiment ... usage
// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace BrowserPagesSignUpFlowExperiment {
  const signupFlowLinkEntry = createEntry(ComponentContentTypes.ComponentLink, 'SignupFlowLink', {
    targetUrl: signupUrl,
    targetTab: 'New ( _blank )',
    internalName: signupUrl,
    appendCurrentQueryParams: true,
  })

  const newCtaEntry = createEntry(ComponentContentTypes.ComponentCtaButton, 'ExperimentalSignupFlowCta', {
    ctaText: {
      data: {},
      content: [
        {
          data: {},
          content: [
            {
              data: {},
              marks: [
                {
                  type: 'bold',
                },
              ],
              value: 'Get Grammarly',
              nodeType: 'text',
            },
            {
              data: {},
              marks: [],
              value: '  It’s free',
              nodeType: 'text',
            },
          ],
          nodeType: BLOCKS.PARAGRAPH,
        },
      ],
      nodeType: BLOCKS.DOCUMENT,
    },
    internalName: 'SignupFlowExperimentalCta',
    color: ComponentCtaButtonColor.WhiteTextGreenButton,
    type: ComponentCtaButtonType.Link,
    targetLink: signupFlowLinkEntry,
  })

  const useGetPatchedCta = (ctaOrVariationContainer: ctaArgType): TypeComponentCtaButton => {
    const { getAuthAndDeviceTypeCta } = useGetAuthAndDeviceTypeCta()
    const cta = getAuthAndDeviceTypeCta(ctaOrVariationContainer)
    if (
      cta.fields.type === ComponentCtaButtonType.ExtensionInstall ||
      cta.fields.type === ComponentCtaButtonType.ExtensionChromeInstall ||
      cta.fields.type === ComponentCtaButtonType.ExtensionEdgeInstall ||
      cta.fields.type === ComponentCtaButtonType.ExtensionSafariInstall ||
      cta.fields.type === ComponentCtaButtonType.ExtensionFirefoxInstall
    ) {
      return patchCTAWithDefaultColor(cta, newCtaEntry)
    }
    return cta
  }

  const patchHeroSection = (heroSection: TypePageFields['heroSection']): TypePageFields['heroSection'] => {
    if (isTypeSectionHero(heroSection)) {
      return {
        ...heroSection,
        fields: {
          ...(heroSection.fields || {}),
          ctAs: heroSection.fields?.ctAs?.map(useGetPatchedCta),
        },
      } as TypeSectionHero
    } else if (isTypeSectionGroup(heroSection)) {
      return {
        ...heroSection,
        fields: {
          ...(heroSection.fields || {}),
          rows: heroSection.fields?.rows?.map(patchHeroSection),
        },
      } as TypeSectionGroup
    }
    return heroSection
  }

  const patchSections = (pageStructure: TypePage): AllowedPageSection[] => {
    return (
      pageStructure.fields.sections?.map((section) => {
        if (isTypeSectionSingleColumn(section)) {
          return {
            ...section,
            fields: {
              ...section.fields,
              content: section.fields.content?.map((item) =>
                isTypeComponentCtaButton(item) ? useGetPatchedCta(item) : item
              ),
            },
          } as TypeSectionSingleColumn
        }

        return section
      }) || []
    )
  }

  export const patchPageStructure: PatchPageStructure = (pageStructure, experimentService) => {
    if (experimentService.isBrowserPagesSignUpFlowEnabled) {
      // Patch header CTAs
      const patchedHeaderCtas = pageStructure.fields?.header?.fields?.stickyCtas?.map((cta) =>
        isTypeComponentDynamic(cta) ? cta : useGetPatchedCta(cta)
      )
      // Patch Hero section CTAs
      const patchedHeroSection = patchHeroSection(pageStructure.fields?.heroSection)
      // Patch sections
      const patchedSections: AllowedPageSection[] = patchSections(pageStructure)

      return {
        ...pageStructure,
        fields: {
          ...pageStructure.fields,
          header: {
            ...(pageStructure.fields?.header || {}),
            fields: {
              ...(pageStructure.fields?.header?.fields || {}),
              stickyCtas: patchedHeaderCtas,
            },
          },
          heroSection: patchedHeroSection,
          sections: patchedSections,
        },
      }
    }
    return pageStructure
  }
}
